import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { login, verifyAuth } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Admin';
    // Check if already logged in
    const token = localStorage.getItem('visualte_admin_token');
    if (token) {
      verifyAuth()
        .then(() => navigate('/admin/dashboard'))
        .catch(() => localStorage.removeItem('visualte_admin_token'));
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password.');
      return;
    }
    setLoading(true);
    try {
      const res = await login(email, password);
      localStorage.setItem('visualte_admin_token', res.data.token);
      toast.success('Logged in successfully.');
      navigate('/admin/dashboard');
    } catch {
      toast.error('Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070A0E] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-[hsl(220,16%,7%)] border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center">
                <Lock className="h-5 w-5 text-[#FF6B00]" />
              </div>
              <div>
                <CardTitle className="text-lg text-white">Admin Access</CardTitle>
                <CardDescription className="text-white/50 text-xs">Restricted area</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label className="text-xs text-white/55 mb-1.5 block">Email</label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@visualte.com"
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#FF6B00] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                />
              </div>
              <div>
                <label className="text-xs text-white/55 mb-1.5 block">Password</label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#FF6B00] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 font-semibold disabled:opacity-50"
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
